import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";

export interface ServerActionResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface UseServerMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<ServerActionResult<TData>>;
  successMessage?: string | ((data: TData) => string);
  errorMessage?: string | ((error: string) => string);
  invalidateQueries?: string[][];
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: Error, variables: TVariables) => void;
}

export function useServerMutation<TData = any, TVariables = any>({
  mutationFn,
  successMessage,
  errorMessage,
  invalidateQueries = [],
  showSuccessToast = true,
  showErrorToast = true,
  onSuccess: customOnSuccess,
  onError: customOnError,
}: UseServerMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables, unknown>({
    mutationFn: async (variables: TVariables) => {
      const result = await mutationFn(variables);

      if (!result.success) {
        throw new Error(result.error || "An error occurred");
      }

      return result.data as TData;
    },
    onSuccess: (data, variables, context) => {
      // Show success toast
      if (showSuccessToast) {
        const message =
          typeof successMessage === "function"
            ? successMessage(data)
            : successMessage || "Operation completed successfully";
        toast.success(message);
      }

      // Invalidate queries
      if (invalidateQueries.length > 0) {
        invalidateQueries.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Call custom onSuccess
      if (customOnSuccess) {
        customOnSuccess(data, variables);
      }
    },
    onError: (error, variables, context) => {
      // Show error toast
      if (showErrorToast) {
        const message =
          typeof errorMessage === "function"
            ? errorMessage(error.message)
            : errorMessage || error.message || "An error occurred";
        toast.error(message);
      }

      // Call custom onError
      if (customOnError) {
        customOnError(error, variables);
      }
    },
  });
}
